import { useEffect, useRef, useState, useCallback } from 'react';
import { useWindowSize, useDebounceCallback, useResizeObserver } from 'usehooks-ts';
import clsx from 'clsx';
import Block from './components/block';
import Button from './components/button';
import Header from './components/header';
import Image from './components/image';
import Main from './components/main';
import ScrollButton from './components/scroll-button';
import Text from './components/text';
import Title from './components/title';
import useAppRefs from '@/hooks/useAppRefs';
import { useAppDispatch, useAppSelector } from '@/redux/store';
import { ConfigSlice } from '@/redux/config';
import { GeneralSlice } from '@/redux/general';
import { cn, getElementSize, setElementStyles } from '@/utils/helper';
import v1 from './article-v1.module.css';
import v2 from './article-v2.module.css';

const DEBOUNCE_DELAY = 100;

export default function Article() {
  const { width, height } = useWindowSize({ initializeWithValue: true, debounceDelay: 300 });
  const [isScrollVisible, setIsScrollVisible] = useState(false);
  const [elementHeights, setElementHeights] = useState({ header: 0, content: 0, scroll: 0 });

  const dispatch = useAppDispatch();
  const article = useAppSelector(ConfigSlice.selectors.article);
  const cssVersion = useAppSelector(ConfigSlice.selectors.cssVersion);
  const withArticle = useAppSelector(GeneralSlice.selectors.withArticle);
  const page = useAppSelector(GeneralSlice.selectors.page);
  const { headerRef, contentRef } = useAppRefs();

  // Create references for <Article />
  const lineRef = useRef<HTMLHRElement>(null);
  const articleRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Memoize scroll functions to prevent unnecessary re-renders
  const scrollToTop = useCallback(() => {
    window.scrollTo({ behavior: 'smooth', top: 0 });
  }, []);

  const scrollToContent = useCallback(() => {
    if (articleRef.current) {
      window.scrollTo({
        top: articleRef.current.getBoundingClientRect().top + window.scrollY,
        behavior: 'smooth',
      });
    }
  }, []);

  // Debounced resize handlers for performance
  const onHeaderResize = useDebounceCallback(
    ({ height }) => setElementHeights((prev) => ({ ...prev, header: height || 0 })),
    DEBOUNCE_DELAY,
  );

  const onContentResize = useDebounceCallback(
    ({ height }) => setElementHeights((prev) => ({ ...prev, content: height || 0 })),
    DEBOUNCE_DELAY,
  );

  // Apply observers on header and content elements resize
  useResizeObserver({ ref: headerRef, box: 'border-box', onResize: onHeaderResize });
  useResizeObserver({ ref: contentRef, box: 'border-box', onResize: onContentResize });

  // Determine if the article should be rendered
  const shouldBeRendered = () => {
    const search = new URLSearchParams(location.search);
    const isDankePhp = window.location.pathname.indexOf('/danke.php') >= 0;
    return Boolean(article) && !Array.from(search.keys()).some((key) => key.startsWith('utm_')) && !isDankePhp;
  };

  // Inform application context if <Article /> is rendered
  useEffect(() => {
    dispatch(GeneralSlice.actions.setWithArticle(shouldBeRendered()));
  }, [article, window.location.pathname]);

  // Update scroll height on mount and when scrollRef is available
  useEffect(() => {
    const height = getElementSize(scrollRef, 'height');
    setElementHeights((prev) => ({ ...prev, scroll: height }));
    setElementStyles(lineRef, { marginTop: isScrollVisible ? height * 3 : height * 1.5 });
  }, [scrollRef, isScrollVisible]);

  // Recalculate scroll visibility when window size or element heights change
  useEffect(() => {
    const { header, content, scroll } = elementHeights;
    setIsScrollVisible(window.innerHeight - header - content >= scroll * 1.25);
  }, [width, height, elementHeights]);

  if (!withArticle || !article) return null;

  return (
    <div className={cn(v1.Article, v2.Article)}>
      <div
        ref={scrollRef}
        className={clsx(
          cn(v1.ScrollButton, v2.ScrollButton),
          isScrollVisible
            ? cn(v1.ScrollButtonVisible, v2.ScrollButtonVisible)
            : cn(v1.ScrollButtonHidden, v2.ScrollButtonHidden),
        )}
      >
        <ScrollButton content={article.scroll()} onClick={scrollToContent} />
      </div>
      <hr
        ref={lineRef}
        className={clsx(cn(v1.Line, v2.Line), {
          [cn(v1.LineLandingPage, v2.LineLandingPage)]: cssVersion === 2 && page === 'landing-page',
        })}
      />
      <div ref={articleRef} className={cn(v1.Content, v2.Content)}>
        <Header content={article.title()} />
        <Main>
          {article.blocks.map((block, index) => (
            <Block
              key={index}
              imagePosition={index % 2 ? 'right' : 'left'}
              title={<Title content={block.title()} />}
              text={<Text content={block.text({ scrollToTop })} />}
              image={<Image src={block.image} />}
              button={<Button content={block.button()} onClick={scrollToTop} />}
            />
          ))}
        </Main>
      </div>
    </div>
  );
}
